// Cheque Book Services (spec §5.1): Request Cheque Book · View Cheque Status ·
// Stop Payment · Positive Pay. The tracker of any in-progress book sits on top.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  chequeBooks, chequeLeaves, chequeBookQuote, requestChequeBook,
  stopCheques, revokeStop, positivePay, addPositivePay, cancelPositivePay,
  accountFacts,
} from '@/api/bankServices';
import { runBankClock } from '@/mock/phase4';
import type { ChequeBook, ChequeLeaf, PositivePayEntry } from '@/types/phase4.types';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import { formatIndianCurrency } from '@/utils/currency';
import '../phase4.css';

const STAGES = ['REQUESTED', 'PRINTED', 'DISPATCHED', 'DELIVERED'];

const STATUS_LABEL: Record<ChequeLeaf['status'], string> = {
  UNUSED: 'Not used yet', PAID: 'Paid', IN_CLEARING: 'In clearing', STOPPED: 'Stopped', RETURNED: 'Returned',
};

const Cheques = () => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [books, setBooks] = useState<ChequeBook[] | null>(null);
  const [product, setProduct] = useState('');

  const load = () => {
    runBankClock();
    chequeBooks().then(setBooks).catch(() => setBooks([]));
    if (accountFacts) setProduct(accountFacts().productType);
  };
  useEffect(load, []);

  const inProgress = books?.find((b) => !b.delivered);

  const menu = [
    { id: 'request', title: 'Request Cheque Book', sub: 'Free once a year, then ₹3 a leaf' },
    { id: 'status', title: 'View Cheque Status', sub: 'Single, range or a whole book' },
    { id: 'stop', title: 'Stop Payment of Cheque', sub: '₹100 a cheque, ₹500 a range' },
    { id: 'positive-pay', title: 'Positive Pay', sub: 'Confirm cheques of ₹50,000 or more' },
  ];

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => (section ? navigate('/profile/bank/cheques') : navigate('/profile/bank'))}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Cheque Book Services</h2>
      </div>

      {inProgress && (
        <div className="nova-p4-card">
          <div className="nova-p4-row-title">Cheque book on its way · {inProgress.reference}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {STAGES.map((s) => (
              <span key={s} className={`nova-p4-status ${STAGES.indexOf(s) <= STAGES.indexOf(inProgress.stage) ? 'ok' : 'info'}`}>
                {s === 'DISPATCHED' && inProgress.awb ? `Dispatched ${inProgress.awb}` : s}
              </span>
            ))}
          </div>
        </div>
      )}

      {!section && menu.map((m) => (
        <button key={m.id} type="button" className="nova-p4-row" onClick={() => navigate(`/profile/bank/cheques/${m.id}`)}>
          <span className="nova-p4-row-text">
            <span className="nova-p4-row-title">{m.title}</span>
            <span className="nova-p4-row-sub">{m.sub}</span>
          </span>
        </button>
      ))}

      {section === 'request' && <RequestBook product={product} onDone={load} />}
      {section === 'status' && <Status books={books ?? []} loadLeaves={chequeLeaves} />}
      {section === 'stop' && <Stop onDone={load} />}
      {section === 'positive-pay' && <PositivePay onDone={load} />}
    </div>
  );
};

// ---------- Request ----------

const RequestBook = ({ product, onDone }: { product: string; onDone: () => void }) => {
  const [leaves, setLeaves] = useState<25 | 50>(25);
  const [quote, setQuote] = useState<{ freeLeft: number; costRupees: number; gstRupees: number; blockedReason?: string } | null>(null);

  useEffect(() => {
    chequeBookQuote(leaves).then(setQuote).catch(() => setQuote(null));
  }, [leaves]);

  const auth = usePaymentAuth(async () => {
    try {
      await requestChequeBook(leaves);
      toast.success('Cheque book requested');
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not request the book.');
    }
  });

  const submit = async () => {
    const total = (quote?.costRupees ?? 0) + (quote?.gstRupees ?? 0);
    const e = await auth.start('NOVABANK', `Cheque book (${leaves} leaves)${total > 0 ? ` ${formatIndianCurrency(total)}` : ''}`);
    if (e) toast.error(e);
  };

  const total = (quote?.costRupees ?? 0) + (quote?.gstRupees ?? 0);
  const used = 1 - (quote?.freeLeft ?? 0);

  return (
    <div className="nova-p4-card">
      <h3>Request Cheque Book</h3>
      <div className="nova-p4-field">
        <label htmlFor="cb_leaves">Leaves</label>
        <select id="cb_leaves" className="form-select" value={leaves} onChange={(e) => setLeaves(Number(e.target.value) as 25 | 50)}>
          <option value={25}>25 leaves</option>
          <option value={50}>50 leaves</option>
        </select>
      </div>
      <div className="nova-p4-moneyline muted"><span>Name on cheque</span><span>{product === 'CURRENT' ? 'Trade name' : 'Account holder'}</span></div>
      <div className="nova-p4-banner blue" style={{ marginTop: 8 }}>
        New cheque book will be sent to the address below — your communication address.{' '}
        <a href="/profile/bank/rekyc/address" onClick={(e) => e.preventDefault()}>Postal address has changed?</a>
      </div>
      <div className="nova-p4-moneyline muted"><span>Charges</span><span>{quote?.freeLeft ? `Free: ${used} of 1 book/year used` : `${formatIndianCurrency(quote?.costRupees ?? 0)} + GST ${formatIndianCurrency(quote?.gstRupees ?? 0)}`}</span></div>
      {!quote?.freeLeft && <div className="nova-p4-moneyline total"><span>Total</span><span>{formatIndianCurrency(total)}</span></div>}
      {quote?.blockedReason && <div className="nova-p4-banner red" style={{ marginTop: 8 }}>{quote.blockedReason}</div>}
      <button type="button" className="nova-p4-primary" style={{ marginTop: 12 }} disabled={!!quote?.blockedReason} onClick={() => void submit()}>
        Deliver Cheque Book
      </button>
      <PaymentAuthSheets authState={auth.authState} approvalLine="Request cheque book" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </div>
  );
};

// ---------- Status ----------

const Status = ({ books, loadLeaves }: { books: ChequeBook[]; loadLeaves: (id: string) => Promise<ChequeLeaf[]> }) => {
  const [bookId, setBookId] = useState('');
  const [leaves, setLeaves] = useState<ChequeLeaf[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!bookId && books.length > 0) setBookId(books[books.length - 1].id);
  }, [books, bookId]);

  useEffect(() => {
    if (bookId) loadLeaves(bookId).then(setLeaves).catch(() => setLeaves([]));
  }, [bookId, loadLeaves]);

  const visible = filter ? leaves.filter((l) => l.status === filter) : leaves;

  return (
    <div className="nova-p4-card">
      <h3>View Cheque Status</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select className="form-select" value={bookId} onChange={(e) => setBookId(e.target.value)} aria-label="Book">
          {books.filter((b) => b.delivered).map((b) => <option key={b.id} value={b.id}>Book {b.from}–{b.to}</option>)}
        </select>
        <select className="form-select" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Status">
          <option value="">All</option>
          {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s as ChequeLeaf['status']]}</option>)}
        </select>
      </div>
      {visible.map((l) => (
        <div key={l.number} className="nova-p4-row" style={{ cursor: 'default' }}>
          <span className="nova-p4-row-text">
            <span className="nova-p4-row-title">Cheque {l.number}</span>
            <span className="nova-p4-row-sub">
              {STATUS_LABEL[l.status]}
              {l.status === 'PAID' && l.paidAt ? ` · ${new Date(l.paidAt).toLocaleDateString('en-IN')}${l.amountRupees ? ` · ${formatIndianCurrency(l.amountRupees)}` : ''}${l.payee ? ` · ${l.payee}` : ''}` : ''}
              {l.status === 'STOPPED' && l.stoppedAt ? ` · ${new Date(l.stoppedAt).toLocaleDateString('en-IN')} · ${l.stopReason}` : ''}
              {l.status === 'RETURNED' && l.returnReason ? ` · ${l.returnReason}` : ''}
            </span>
          </span>
          {l.status === 'STOPPED' && (
            <button type="button" className="nova-p4-link" onClick={() => void revokeStop(l.number).then(() => loadLeaves(bookId).then(setLeaves))}>Revoke stop</button>
          )}
        </div>
      ))}
      {visible.length === 0 && <div className="nova-p4-empty">No cheques here.</div>}
    </div>
  );
};

// ---------- Stop ----------

const Stop = ({ onDone }: { onDone: () => void }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('Lost');
  const [error, setError] = useState('');

  const auth = usePaymentAuth(async () => {
    try {
      const res = await stopCheques({ from: Number(from), to: Number(to || from), reason });
      toast.success(`Stopped · ${res.reference}`);
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not stop the cheque.');
    }
  });

  const isRange = to && Number(to) > Number(from);
  const count = isRange ? Number(to) - Number(from) + 1 : 1;
  const cost = (isRange ? 500 : 100) * 1.18;

  const submit = async () => {
    setError('');
    if (!from) return setError('Enter the cheque number.'), undefined;
    const e = await auth.start('NOVABANK', `Stop ${isRange ? `${count} cheques` : `cheque ${from}`} ${formatIndianCurrency(cost)}`);
    if (e) setError(e);
    return undefined;
  };

  return (
    <div className="nova-p4-card">
      <h3>Stop Payment of Cheque</h3>
      <div className="nova-p4-field">
        <label htmlFor="sp_from">From cheque number</label>
        <input id="sp_from" className="form-control" type="number" value={from} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div className="nova-p4-field">
        <label htmlFor="sp_to">To (for a range, up to 100)</label>
        <input id="sp_to" className="form-control" type="number" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div className="nova-p4-field">
        <label htmlFor="sp_reason">Reason</label>
        <select id="sp_reason" className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
          {['Lost', 'Stolen', 'Payment dispute', 'Wrong details', 'Other'].map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>
      <div className="nova-p4-moneyline muted"><span>Charges</span><span>{isRange ? '₹500 per range' : '₹100 per cheque'} + GST = {formatIndianCurrency(cost)}</span></div>
      {error && <div className="nova-p4-banner red">{error}</div>}
      <button type="button" className="nova-p4-primary" onClick={() => void submit()}>Stop {isRange ? 'range' : 'cheque'}</button>
      <PaymentAuthSheets authState={auth.authState} approvalLine="Stop payment" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </div>
  );
};

// ---------- Positive Pay ----------

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const twoDigit = (n: number): string => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ' ' + ONES[n % 10] : ''}`);
const threeDigit = (n: number): string => `${n >= 100 ? ONES[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' : '') : ''}${n % 100 ? twoDigit(n % 100) : ''}`;

/** Indian-style amount in words (spec §5.1). */
export const amountInWords = (rupees: number): string => {
  if (rupees <= 0) return '';
  const n = Math.floor(rupees);
  const crore = Math.floor(n / 1e7);
  const lakh = Math.floor((n % 1e7) / 1e5);
  const thousand = Math.floor((n % 1e5) / 1000);
  const rest = n % 1000;
  const parts = [
    crore ? `${threeDigit(crore)} Crore` : '',
    lakh ? `${twoDigit(lakh)} Lakh` : '',
    thousand ? `${twoDigit(thousand)} Thousand` : '',
    rest ? threeDigit(rest) : '',
  ].filter(Boolean);
  return `Rupees ${parts.join(' ')} Only`;
};

const PositivePay = ({ onDone }: { onDone: () => void }) => {
  const [entries, setEntries] = useState<PositivePayEntry[]>([]);
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [payee, setPayee] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const load = () => positivePay().then(setEntries).catch(() => setEntries([]));
  useEffect(() => { load(); }, []);

  const auth = usePaymentAuth(async () => {
    try {
      await addPositivePay({ chequeNumber: Number(chequeNumber), chequeDate, payee, amountRupees: Number(amount) });
      toast.success('Positive Pay registered');
      setChequeNumber(''); setChequeDate(''); setPayee(''); setAmount('');
      load();
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not register.');
    }
  });

  const submit = async () => {
    setError('');
    const e = await auth.start('NOVABANK', `Positive Pay cheque ${chequeNumber} ${formatIndianCurrency(Number(amount))}`);
    if (e) setError(e);
  };

  const mandatory = Number(amount) >= 500_000;

  return (
    <>
      <div className="nova-p4-banner blue">Offered from ₹50,000; mandatory from ₹5 lakh. Submit before the cheque is presented. Cheques clear the same working day.</div>
      <div className="nova-p4-card">
        <h3>Register Positive Pay</h3>
        <div className="nova-p4-field"><label htmlFor="pp_num">Cheque number</label><input id="pp_num" className="form-control" type="number" value={chequeNumber} onChange={(e) => setChequeNumber(e.target.value)} /></div>
        <div className="nova-p4-field"><label htmlFor="pp_date">Cheque date</label><input id="pp_date" className="form-control" type="date" value={chequeDate} onChange={(e) => setChequeDate(e.target.value)} /></div>
        <div className="nova-p4-field"><label htmlFor="pp_payee">Payee name</label><input id="pp_payee" className="form-control" value={payee} onChange={(e) => setPayee(e.target.value)} /></div>
        <div className="nova-p4-field">
          <label htmlFor="pp_amt">Amount (figures)</label>
          <input id="pp_amt" className="form-control" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          {Number(amount) > 0 && <div className="hint">{amountInWords(Number(amount))}</div>}
          {mandatory && <div className="err">Positive Pay is mandatory at ₹5 lakh and above.</div>}
        </div>
        {error && <div className="nova-p4-banner red">{error}</div>}
        <button type="button" className="nova-p4-primary" disabled={!chequeNumber || !payee || Number(amount) < 50_000} onClick={() => void submit()}>Register</button>
        <PaymentAuthSheets authState={auth.authState} approvalLine="Positive Pay" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
      </div>

      {entries.length > 0 && (
        <>
          <h3 className="nova-p4-row-title" style={{ margin: '16px 0 10px' }}>Registered</h3>
          {entries.map((e) => (
            <div key={e.id} className="nova-p4-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="nova-p4-row-title">Cheque {e.chequeNumber} · {formatIndianCurrency(e.amountRupees)}</div>
                <span className={`nova-p4-status ${e.status === 'REGISTERED' ? 'info' : e.status === 'PRESENTED_MATCHED' ? 'ok' : e.status === 'CANCELLED' ? 'warn' : 'bad'}`}>
                  {e.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="nova-p4-row-sub">{e.payee} · {e.chequeDate}</div>
              {e.status === 'REGISTERED' && (
                <button type="button" className="nova-p4-link" style={{ color: '#c0392b', marginTop: 8 }} onClick={() => void cancelPositivePay(e.id).then(load)}>Cancel</button>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
};

export default Cheques;
